import { isUnmeasured, toneFor, type StatusState } from '../data/state'

/*
 * The signature element of this page: a 3px marker in the inline-start gutter
 * of every component and region row, full row height, coloured by state — and
 * **hatched, not coloured, when the state is `unknown`**.
 *
 * ── Why not a status dot ───────────────────────────────────────────────────
 *
 * Every status page on the internet is a column of coloured dots, and a dot is
 * structurally unable to say the one thing this product insists on saying.
 * `DESIGN_SYSTEM.md` §3.4: "unmeasured is not a status" — a `null` is hatched,
 * never coloured — and V1_2_SPEC.md §5.1 applies exactly that to this surface:
 * nothing unmeasured or unreachable is ever rendered as operational, and
 * `unknown` is rendered as itself rather than as a shade of something else.
 *
 * The hatch is a 45° pattern on a 2px pitch. Inside an 8px dot that is two
 * stripes and reads as a texture-less smudge; the pattern only becomes legible
 * at a size a dot does not have — this marker is 6px wide and the full height of
 * its row, which is enough for the stripes to be stripes. So the geometry follows the requirement rather
 * than the convention: a tall thin marker has the room, and it puts every row's
 * marker on one vertical line, so a reader scanning the gutter sees at a glance
 * whether anything is hatched — whether, anywhere on this page, nobody knows.
 *
 * That is the distinction the page exists to preserve. Severity is carried in
 * hue; *knowledge* is carried in whether there is a hue at all. A reader can
 * take both in without reading a word, and neither can be mistaken for the
 * other.
 *
 * ── It is decorative, and that is a claim about the markup ─────────────────
 *
 * `aria-hidden`, because the state is already beside it as real text — §4's
 * requirement that status be conveyed by text as well as colour is met by the
 * label, not by an `aria-label` on a stripe. Announcing "hatched marker" would
 * add a second, worse copy of what the row already says.
 */

const TONE_CLASS = {
  positive: 'bg-positive-fill',
  caution: 'bg-caution-fill',
  critical: 'bg-critical-fill',
  /* No colour. `hatch` is `packages/ui`'s utility for §3.4's pattern, drawn in
     `--border-strong` — a graphical object carrying information, at 3:1. */
  unmeasured: 'hatch',
} as const

export function StateMarker({ state }: { readonly state: StatusState }) {
  return (
    <span
      aria-hidden="true"
      data-state={state}
      data-unmeasured={isUnmeasured(state) ? 'true' : undefined}
      className={`w-1.5 shrink-0 self-stretch ${TONE_CLASS[toneFor(state)]}`}
    />
  )
}

/** The same marker at text height, for a headline rather than a row. */
export function StateMarkerInline({ state }: { readonly state: StatusState }) {
  return (
    <span
      aria-hidden="true"
      data-state={state}
      className={`h-7 w-1.5 shrink-0 ${TONE_CLASS[toneFor(state)]}`}
    />
  )
}
